import React, { useMemo, useRef } from 'react';
import { Typography, Button, Tooltip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { useTranslation } from 'react-i18next';
import type { HealthRecord } from '../../services/lifeDataService';

/* ── Glucose slots ─────────────────────────────────────────────────────────── */

export const GLUCOSE_SLOTS = [
  '空腹(早餐前)', '早餐後', '中餐前', '中餐後', '晚餐前', '晚餐後',
] as const;
export type GlucoseSlot = typeof GLUCOSE_SLOTS[number];

const GLUCOSE_SLOT_EN: Record<GlucoseSlot, string> = {
  '空腹(早餐前)': 'Fasting (Pre-Breakfast)',
  '早餐後':        'After Breakfast',
  '中餐前':        'Before Lunch',
  '中餐後':        'After Lunch',
  '晚餐前':        'Before Dinner',
  '晚餐後':        'After Dinner',
};

/* ── Blood pressure ────────────────────────────────────────────────────────── */

export interface BloodPressure {
  systolic:  number;
  diastolic: number;
  heartRate: number;
}

export function bpColor(sys: number, dia: number): string {
  if (sys >= 140 || dia >= 90) return '#ef5350';
  if (sys >= 120 || dia >= 80) return '#ffa726';
  return '#66bb6a';
}

/* ── Daily row ─────────────────────────────────────────────────────────────── */

export interface DailyRow {
  date:    string;
  glucose: Partial<Record<GlucoseSlot, number>>;
  bp:      BloodPressure | null;
}

/* ── Pivot builder ─────────────────────────────────────────────────────────── */

export function buildDailySummary(records: HealthRecord[]): DailyRow[] {
  const glucose = records.filter(r => r.type === '血糖' && r.values[0] != null);
  const bpRecs  = records.filter(r => r.type === '血壓' && r.values.length >= 3);

  // glucose: date → slot → latest value (sort desc → first-seen wins)
  const gMap = new Map<string, Partial<Record<GlucoseSlot, number>>>();
  for (const r of [...glucose].sort((a, b) => b.ts.localeCompare(a.ts))) {
    const date = r.ts.slice(0, 10);
    if (!(GLUCOSE_SLOTS as readonly string[]).includes(r.condition)) continue;
    const slot = r.condition as GlucoseSlot;
    if (!gMap.has(date)) gMap.set(date, {});
    const row = gMap.get(date)!;
    if (row[slot] === undefined) row[slot] = r.values[0];
  }

  // bp: date → average of all measurements, rounded to integer
  const bGroup = new Map<string, HealthRecord[]>();
  for (const r of bpRecs) {
    const date = r.ts.slice(0, 10);
    if (!bGroup.has(date)) bGroup.set(date, []);
    bGroup.get(date)!.push(r);
  }
  const bMap = new Map<string, BloodPressure>();
  Array.from(bGroup.entries()).forEach(([date, recs]) => {
    const n   = recs.length;
    const sys = recs.reduce((s: number, r: HealthRecord) => s + r.values[0], 0) / n;
    const dia = recs.reduce((s: number, r: HealthRecord) => s + r.values[1], 0) / n;
    const hr  = recs.reduce((s: number, r: HealthRecord) => s + r.values[2], 0) / n;
    bMap.set(date, {
      systolic:  Math.round(sys),
      diastolic: Math.round(dia),
      heartRate: Math.round(hr),
    });
  });

  const allDates = new Set<string>(
    Array.from(gMap.keys()).concat(Array.from(bMap.keys()))
  );
  return Array.from(allDates).sort().map(date => ({
    date,
    glucose: gMap.get(date) ?? {},
    bp:      bMap.get(date) ?? null,
  }));
}

/* ── Colour coding ─────────────────────────────────────────────────────────── */

function glucoseColor(v: number): string {
  if (v > 200) return '#ef5350';
  if (v >= 126) return '#ffa726';
  return '#66bb6a';
}

/* ── Props ─────────────────────────────────────────────────────────────────── */

interface Props {
  records:   HealthRecord[];
  dateRange?: { from?: string; to?: string };
}

/* ── Component ─────────────────────────────────────────────────────────────── */

const DailyHealthSummaryTable: React.FC<Props> = ({ records, dateRange }) => {
  const { i18n } = useTranslation();
  const isZh = !i18n.language.startsWith('en');

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: isZh ? '每日健康量測表' : 'Daily Health Summary',
    pageStyle: `
      @page { size: A4 portrait; margin: 15mm; }
      @media print {
        body { font-size: 11pt; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ccc; padding: 4px 6px; font-size: 9pt; }
      }
    `,
  });

  const rows = useMemo(() => buildDailySummary(records), [records]);

  const rangeLabel = useMemo(() => {
    const from = dateRange?.from;
    const to   = dateRange?.to;
    if (!from && !to) return '';
    if (from && to) return `${from} ~ ${to}`;
    if (from) return `${from} ~`;
    return `~ ${to}`;
  }, [dateRange]);

  if (rows.length === 0) return null;

  const cellStyle: React.CSSProperties = {
    padding: '6px 10px',
    textAlign: 'center',
    fontSize: 13,
    borderBottom: '1px solid #f0f0f0',
    minWidth: 68,
  };

  const headerCellStyle: React.CSSProperties = {
    padding: '8px 10px',
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    background: '#e91e63',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ marginTop: 24 }}>
      {/* Section header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12,
      }}>
        <div>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a237e' }}>
            {isZh ? '每日健康量測表' : 'Daily Health Summary'}
          </Typography>
          {rangeLabel && (
            <Typography sx={{ fontSize: 12, color: '#9e9e9e', mt: 0.3 }}>
              {rangeLabel}
            </Typography>
          )}
        </div>
        <Tooltip title={isZh ? '列印' : 'Print'}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => handlePrint()}
            className="no-print"
            sx={{
              borderColor: '#e91e63',
              color: '#e91e63',
              '&:hover': { borderColor: '#c2185b', color: '#c2185b', background: '#fce4ec' },
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {isZh ? '列印' : 'Print'}
          </Button>
        </Tooltip>
      </div>

      {/* Printable area */}
      <div ref={printRef}>
        {/* Print-only title */}
        <div className="print-only" style={{ display: 'none', marginBottom: 16 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isZh ? '每日健康量測表' : 'Daily Health Summary'}
          </Typography>
          {rangeLabel && (
            <Typography sx={{ fontSize: 12, color: '#666' }}>{rangeLabel}</Typography>
          )}
        </div>

        <div style={{
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...headerCellStyle, textAlign: 'left', minWidth: 100 }}>
                  {isZh ? '日期' : 'Date'}
                </th>
                {GLUCOSE_SLOTS.map(slot => (
                  <th key={slot} style={headerCellStyle}>
                    {isZh ? slot : GLUCOSE_SLOT_EN[slot]}
                  </th>
                ))}
                <th style={{ ...headerCellStyle, background: '#5c6bc0' }}>
                  {isZh ? '血壓(平均)' : 'BP (avg)'}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row.date}
                  style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}
                >
                  <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, color: '#1565c0' }}>
                    {row.date}
                  </td>
                  {GLUCOSE_SLOTS.map(slot => {
                    const val = row.glucose[slot];
                    return (
                      <td key={slot} style={cellStyle}>
                        {val !== undefined ? (
                          <span style={{ fontWeight: 700, color: glucoseColor(val) }}>
                            {val}
                          </span>
                        ) : (
                          <span style={{ color: '#ccc' }}>—</span>
                        )}
                      </td>
                    );
                  })}
                  <td style={cellStyle}>
                    {row.bp ? (
                      <span>
                        <span style={{
                          color: bpColor(row.bp.systolic, row.bp.diastolic),
                          fontWeight: 700,
                        }}>
                          {row.bp.systolic}/{row.bp.diastolic}
                        </span>
                        <span style={{ color: '#999', fontSize: '0.85em', marginLeft: 4 }}>
                          · {row.bp.heartRate} bpm
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: '#ccc' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: '#9e9e9e', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: '#555' }}>{isZh ? '血糖：' : 'Glucose:'}</span>
          <span>
            <span style={{ color: '#66bb6a', fontWeight: 700 }}>●</span>
            {isZh ? ' ＜126 正常' : ' <126 Normal'}
          </span>
          <span>
            <span style={{ color: '#ffa726', fontWeight: 700 }}>●</span>
            {isZh ? ' 126–200 偏高' : ' 126–200 High'}
          </span>
          <span>
            <span style={{ color: '#ef5350', fontWeight: 700 }}>●</span>
            {isZh ? ' ＞200 過高' : ' >200 Very High'}
          </span>
          <span style={{ marginLeft: 8, fontWeight: 600, color: '#555' }}>{isZh ? '血壓：' : 'BP:'}</span>
          <span>
            <span style={{ color: '#66bb6a', fontWeight: 700 }}>●</span>
            {isZh ? ' ＜120/80 正常' : ' <120/80 Normal'}
          </span>
          <span>
            <span style={{ color: '#ffa726', fontWeight: 700 }}>●</span>
            {isZh ? ' 120/80–139/89 前期' : ' 120/80–139/89 Pre-HT'}
          </span>
          <span>
            <span style={{ color: '#ef5350', fontWeight: 700 }}>●</span>
            {isZh ? ' ≥140/90 高血壓' : ' ≥140/90 Hypertension'}
          </span>
          <span style={{ marginLeft: 'auto' }}>
            {isZh ? '單位：mg/dL / mmHg　— = 無紀錄' : 'Unit: mg/dL / mmHg  — = no record'}
          </span>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          nav, aside, header, .sidebar, [class*="Sidebar"],
          [class*="TopNav"], [class*="Breadcrumb"],
          [class*="FilterListIcon"], [class*="ToggleButton"],
          [class*="AddIcon"], [class*="DialogRoot"],
          [data-print-hide="true"] { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default DailyHealthSummaryTable;
